import { useMemo, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getAccessibleTextOnBackground, getContrastTextColor } from '../utils/luminance';
import { WaveBand } from './_waveBand';

const imageDef: SlotGroupDefinition = {
  label: 'Overview Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_featured_image_media.alt_text',
      defaultValue: 'Swimming pool',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Select a photo of the swimming pool or pool area.',
    context:
      "Overview section image, next to the community intro copy. Must be a distinct photo — not the property's featured image.",
    sourceCollection: 'photo_gallery',
  },
};

const ctaPrimaryDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const ctaSecondaryDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Label', type: 'text', defaultValue: 'Virtual Tours' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  // A standalone editable image (defaults to the property's featured image),
  // not connected to the photo gallery collection.
  const image = useSlotGroup('propertyoverview.image', imageDef);

  const ctaPrimary = useSlotGroup('propertyoverview.cta_primary', ctaPrimaryDef);
  const ctaSecondary = useSlotGroup('propertyoverview.cta_secondary', ctaSecondaryDef);

  // Body copy reads against `--vibe-color-surface`, but the default
  // `--vibe-color-text` is paired with the page background — a property whose
  // `surface` (property_color_3) is dark leaves the copy dark-on-dark. Keep the
  // themed text when it clears WCAG AA against surface; otherwise fall back to a
  // luminance-picked black/white. Mirrors the banner-text fix in AmenitiesFeatures.
  const tokens = useTokens();
  // Prefer primary on the surface; fall back when contrast is too low.
  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.surface, tokens?.color?.primary),
    [tokens],
  );
  const bodyTextStyle = useMemo<CSSProperties | undefined>(() => {
    const surface = tokens?.color?.surface;
    if (!surface) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(surface, themed) >= 4.5 ? themed : getContrastTextColor(surface);
    return { ['--po-body-text' as string]: color } as CSSProperties;
  }, [tokens]);

  return (
    <section className="relative bg-[var(--vibe-color-surface)]" style={bodyTextStyle}>
      <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-12 md:pt-20 pb-[70px] md:pb-0">
        <div
          ref={revealRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-[0.9fr_1fr] lg:grid-cols-[1.1fr_1fr] gap-8 md:gap-14 items-center md:items-start lg:items-center"
        >
          {/* Image (left) — a standalone editable image. Sits in the z-10
              content layer so its bottom overlaps the wave band below. */}
          {image.image ? (
            <EditableSlotGroup groupPath="propertyoverview.image" definition={imageDef} className="hidden md:block">
              <div className="w-full overflow-hidden">
                <VibesitesImage
                  url={image.image}
                  alt={image.alt || 'Community photo'}
                  className="w-full object-cover aspect-[13/12]"
                  sizes="(min-width: 768px) 55vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
          ) : null}

          {/* Heading + body + CTAs (right) */}
          <div className={image.image ? '' : 'md:col-span-2'}>
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6 md:mb-3 lg:mb-6 leading-tight uppercase [&_a]:underline"
              style={{ color: headingColor }}
            />

            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="font-body text-base text-[color:var(--po-body-text,var(--vibe-color-text))] leading-relaxed [&_a]:underline"
            />

            <div className="mt-8 md:mt-4 lg:mt-8 flex flex-wrap md:flex-nowrap lg:flex-wrap justify-center md:justify-start gap-4">
              <EditableSlotGroup groupPath="propertyoverview.cta_primary" definition={ctaPrimaryDef}>
                <a
                  href={ctaPrimary.url || '/gallery'}
                  target={ctaPrimary._target || '_self'}
                  className="po-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-8 lg:py-3.5 lg:text-base lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase transition-all"
                >
                  {ctaPrimary.label || 'View Gallery'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="propertyoverview.cta_secondary" definition={ctaSecondaryDef}>
                <a
                  href={ctaSecondary.url || '/virtual-tour'}
                  target={ctaSecondary._target || '_self'}
                  className="po-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-8 lg:py-3.5 lg:text-base lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase transition-all"
                >
                  {ctaSecondary.label || 'Virtual Tours'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/* Wave band at the bottom of the stripe. Pulled up with a negative top
          margin and placed BEHIND the content (z-0) so the left image overlaps
          its top edge; on the right the wave shows through the transparent
          content layer. A lighter (background) fill gives the tonal step the
          reference has. */}
      {/* WaveBand halves its geometry on mobile automatically (see _waveBand). */}
      {/* Tablet (md–lg): push the wave down (positive margin) so it clears the
          CTAs with a ~24px gap; desktop keeps the pulled-up overlap. On mobile
          the image is hidden, so the content adds pb-[70px] which — against the
          wave's -mt-10 (40px) pull-up — leaves a ~30px gap above the centered CTAs. */}
      <WaveBand height={200} spacing={15} amp={25} className="relative z-0 w-full bg-[var(--vibe-color-background)] -mt-10 md:mt-6 lg:-mt-12" />

      <style>{`
        /* Richtext links inherit the heading/body color instead of the
           global --vibe-color-hyperlink token (or a leftover primary tint). */
        [data-vibesite-slot="PropertyOverview"] [data-vibesite-richtext] a,
        [data-vibesite-slot="PropertyOverview"] [data-vibesite-richtext] a:hover,
        [data-vibesite-slot="PropertyOverview"] [data-vibesite-richtext] a:visited {
          color: inherit !important;
        }
        .po-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          border-radius: 9999px;
        }
        .po-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          transform: translateY(-1px);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue:
        '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p><p>Modern homes, resort-inspired amenities, and a location that puts the best of the neighborhood right at your doorstep.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.image': imageDef,
    'propertyoverview.cta_primary': ctaPrimaryDef,
    'propertyoverview.cta_secondary': ctaSecondaryDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
